/**
 * Circular progress indicator for overall analysis completion.
 *
 * Renders an SVG ring that fills proportionally to ``percentage``.
 */

interface Props {
  /** 0-100 */
  percentage: number;
  /** Diameter in pixels. */
  size?: number;
  /** Stroke width in pixels. */
  strokeWidth?: number;
  /** Optional label shown inside the ring. */
  label?: string;
}

export function AnalysisProgressRing({
  percentage,
  size = 120,
  strokeWidth = 10,
  label,
}: Props) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(percentage, 100) / 100) * circumference;

  // Color shifts from blue to green as progress increases.
  const color =
    percentage >= 100
      ? '#22c55e' // green-500
      : percentage >= 50
        ? '#3b82f6' // blue-500
        : '#6366f1'; // indigo-500

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-500 ease-in-out"
        />
      </svg>
      {/* Center label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {Math.round(percentage)}%
        </span>
        {label && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
        )}
      </div>
    </div>
  );
}
